import { useEffect, useRef } from 'react';
import ApexCharts from 'apexcharts';
import { FarmlandWeatherHourListItemVo } from '@/api/growth/dashboard/type';

interface GrowthDashboardWeatherHourCardProps {
    hourList: FarmlandWeatherHourListItemVo[];
}

const GrowthDashboardWeatherHourCard = ({
    hourList,
}: GrowthDashboardWeatherHourCardProps) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<ApexCharts | null>(null);

    useEffect(() => {
        if (!chartRef.current || !hourList || hourList.length === 0) return;

        // 시간순으로 정렬
        const sortedData = [...hourList].sort((a, b) =>
            a.wTime.localeCompare(b.wTime),
        );

        // 데이터 추출
        const temperatures = sortedData.map(item => item.tmp);
        const rainfalls = sortedData.map(item => item.pcp);
        const humidities = sortedData.map(item => item.reh);
        const labels = sortedData.map(item => {
            const hour = item.wTime.substring(0, 2);
            return `${hour}시`;
        });

        const options = {
            series: [
                {
                    name: '기온',
                    type: 'line',
                    data: temperatures,
                },
                {
                    name: '강수량',
                    type: 'area',
                    data: rainfalls,
                },
                {
                    name: '습도',
                    type: 'line',
                    data: humidities,
                },
            ],
            chart: {
                type: 'line',
                height: 245,
                toolbar: {
                    show: false,
                },
                zoom: {
                    enabled: false,
                },
            },
            dataLabels: {
                enabled: false,
            },
            stroke: {
                width: [3, 1, 2],
                curve: 'smooth',
                dashArray: [0, 3, 5],
            },
            plotOptions: {
                bar: {
                    columnWidth: '60%',
                    borderRadius: 4,
                },
            },
            colors: ['#e4846f', '#37b0dd', '#2acc75'],
            fill: {
                type: ['solid', 'gradient'],
                gradient: {
                    shade: 'dark',
                    shadeIntensity: 0.1,
                    inverseColors: false,
                    gradientToColors: ['rgb(25,100,186)', 'rgb(82,106,228)'],
                    opacityFrom: [1, 0.4],
                    opacityTo: [1, 0.5],
                    stops: [0, 90, 100],
                },
            },
            markers: {
                size: [4, 0, 3],
                strokeWidth: 2,
                hover: {
                    size: 6,
                },
            },
            grid: {
                borderColor: '#f1f1f1',
                strokeDashArray: 3,
                xaxis: {
                    lines: {
                        show: true,
                    },
                },
                yaxis: {
                    lines: {
                        show: true,
                    },
                },
            },
            labels: labels,
            xaxis: {
                labels: {
                    style: {
                        fontSize: '11px',
                        colors: '#6c757d',
                    },
                    rotate: -45,
                    rotateAlways: false,
                },
                axisBorder: {
                    show: true,
                    color: '#e0e0e0',
                },
                axisTicks: {
                    show: true,
                    color: '#e0e0e0',
                },
            },
            yaxis: [
                {
                    seriesName: '기온',
                    title: {
                        text: '기온 (°C)',
                        style: {
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#FF6B6B',
                        },
                    },
                    labels: {
                        formatter: (val: number) => `${val}°`,
                        style: {
                            colors: '#FF6B6B',
                            fontSize: '11px',
                        },
                    },
                },
                {
                    seriesName: '강수량',
                    opposite: true,
                    title: {
                        text: '강수량 (mm)',
                        style: {
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#4ECDC4',
                        },
                    },
                    labels: {
                        formatter: (val: number) => `${val.toFixed(1)}`,
                        style: {
                            colors: '#4ECDC4',
                            fontSize: '11px',
                        },
                    },
                    min: 0,
                },
                {
                    seriesName: '습도',
                    opposite: true,
                    show: false,
                    min: 0,
                    max: 100,
                },
            ],
            tooltip: {
                shared: true,
                intersect: false,
                x: {
                    show: true,
                },
                y: [
                    {
                        formatter: (val: number) => `${val}°C`,
                        title: {
                            formatter: () => '기온: ',
                        },
                    },
                    {
                        formatter: (val: number) => `${val}mm`,
                        title: {
                            formatter: () => '강수량: ',
                        },
                    },
                    {
                        formatter: (val: number) => `${val}%`,
                        title: {
                            formatter: () => '습도: ',
                        },
                    },
                ],
            },
            legend: {
                show: true,
                position: 'top',
                horizontalAlign: 'center',
                fontSize: '12px',
                markers: {
                    width: 10,
                    height: 10,
                    radius: 2,
                },
                itemMargin: {
                    horizontal: 15,
                    vertical: 5,
                },
            },
        };

        chartInstance.current = new ApexCharts(chartRef.current, options);
        chartInstance.current.render();

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [hourList]);

    if (!hourList || hourList.length === 0) {
        return (
            <div className="text-center p-3 text-muted">
                <i className="bi bi-cloud-slash fs-1 d-block mb-2"></i>
                시간대별 날씨 정보가 없습니다.
            </div>
        );
    }

    return <div ref={chartRef}></div>;
};

export default GrowthDashboardWeatherHourCard;
